import { useEffect, useRef, useState } from 'react';
import { getRandomMusicFile } from '../utils/musicUtils';

const MUTED_KEY = 'backgroundMusicMuted';

function readMuted() {
  try {
    return localStorage.getItem(MUTED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Loops one randomly picked music track while `enabled`, stopping on unmount. The mute choice
 * is remembered in localStorage. Browsers block audio until the page has been tapped/clicked,
 * so a blocked start is retried on the next pointerdown.
 */
export default function useBackgroundMusic(enabled, volume = 0.4) {
  const [muted, setMuted] = useState(readMuted);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = new Audio(getRandomMusicFile());
    audio.loop = true;
    audio.volume = volume;
    audioRef.current = audio;
    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, [volume]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    if (!enabled || muted) {
      audio.pause();
      return undefined;
    }
    let cancelled = false;
    const start = () => {
      if (!cancelled) audio.play().catch(() => {});
    };
    audio.play().catch(() => {
      if (!cancelled) document.addEventListener('pointerdown', start, { once: true });
    });
    return () => {
      cancelled = true;
      document.removeEventListener('pointerdown', start);
    };
  }, [enabled, muted, volume]);

  useEffect(() => {
    try {
      localStorage.setItem(MUTED_KEY, String(muted));
    } catch {
      // Storage blocked (e.g. private mode): the choice just isn't remembered.
    }
  }, [muted]);

  return { muted, toggleMuted: () => setMuted((m) => !m) };
}
